import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import portalApi, { resolveFileUrl } from '../../../../lib/portalApi';
import {
  Kanban,
  List,
  Clock,
  AlertCircle,
  CheckCircle,
  FileText,
  Upload,
  PauseCircle,
  Play,
  Send,
  Calendar,
  Flag,
  ArrowRight,
  X,
  History,
  Shield,
  Filter,
  RefreshCw,
  Sparkles,
  Paperclip,
} from 'lucide-react';

export default function StartupRoadmapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'all';

  const [tasks, setTasks] = useState([]);
  const [metrics, setMetrics] = useState({
    total: 0,
    toDo: 0,
    inProgress: 0,
    onHold: 0,
    inReview: 0,
    done: 0,
    overdue: 0,
    urgent: 0,
  });
  const [loading, setLoading] = useState(true);

  // View mode: 'board' or 'list'
  const [viewMode, setViewMode] = useState('board');
  const [activeFilter, setActiveFilter] = useState(initialFilter); // 'all', 'overdue', 'urgent', 'in_review'
  const [sortBy, setSortBy] = useState('deadline'); // 'deadline' or 'priority'

  // Modals state
  const [selectedTask, setSelectedTask] = useState(null); // Task detail drawer/modal
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdTask, setHoldTask] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [holdError, setHoldError] = useState('');

  const [deliverableModalOpen, setDeliverableModalOpen] = useState(false);
  const [deliverableTask, setDeliverableTask] = useState(null);
  const [deliverableFile, setDeliverableFile] = useState(null);
  const [deliverableNotes, setDeliverableNotes] = useState('');
  const [deliverableSubmitting, setDeliverableSubmitting] = useState(false);
  const [deliverableError, setDeliverableError] = useState('');

  const loadRoadmap = async () => {
    try {
      setLoading(true);
      const res = await portalApi.getStartupPortalRoadmap();
      setTasks(res?.tasks || []);
      setMetrics(
        res?.metrics || {
          total: 0,
          toDo: 0,
          inProgress: 0,
          onHold: 0,
          inReview: 0,
          done: 0,
          overdue: 0,
          urgent: 0,
        }
      );
    } catch (err) {
      console.error('Failed to load roadmap tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoadmap();
  }, []);

  useEffect(() => {
    const f = searchParams.get('filter');
    if (f && ['all', 'overdue', 'urgent', 'in_review'].includes(f)) {
      setActiveFilter(f);
    }
  }, [searchParams]);

  const handleFilterChange = (f) => {
    setActiveFilter(f);
    setSearchParams(f === 'all' ? {} : { filter: f });
  };

  // Filtered and sorted tasks
  const filteredTasks = useMemo(() => {
    const now = new Date();
    let list = tasks.filter((t) => {
      const isDone = ['Done', 'Completed'].includes(t.status);
      if (activeFilter === 'overdue') {
        return t.deadline && new Date(t.deadline) < now && !isDone;
      }
      if (activeFilter === 'urgent') {
        return ['High', 'Urgent'].includes(t.priority) && !isDone;
      }
      if (activeFilter === 'in_review') {
        return t.status === 'In Review';
      }
      return true;
    });

    list.sort((a, b) => {
      if (sortBy === 'priority') {
        const order = { Urgent: 4, High: 3, Medium: 2, Low: 1 };
        return (order[b.priority] || 0) - (order[a.priority] || 0);
      }
      // default: deadline
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    });

    return list;
  }, [tasks, activeFilter, sortBy]);

  // Status transitions
  const startTask = async (task) => {
    try {
      await portalApi.updateStartupPortalTaskStatus(task._id, { status: 'In Progress' });
      await loadRoadmap();
    } catch (err) {
      alert(err.message || 'Failed to start milestone.');
    }
  };

  const resumeTask = async (task) => {
    try {
      await portalApi.updateStartupPortalTaskStatus(task._id, { status: 'In Progress' });
      await loadRoadmap();
    } catch (err) {
      alert(err.message || 'Failed to resume milestone.');
    }
  };

  const openHoldModal = (task) => {
    setHoldTask(task);
    setHoldReason('');
    setHoldError('');
    setHoldModalOpen(true);
  };

  const submitHold = async (e) => {
    e.preventDefault();
    if (!holdReason.trim()) {
      setHoldError('A justification reason is required to place a milestone on hold.');
      return;
    }
    try {
      await portalApi.updateStartupPortalTaskStatus(holdTask._id, {
        status: 'On Hold',
        reason: holdReason.trim(),
      });
      setHoldModalOpen(false);
      await loadRoadmap();
    } catch (err) {
      setHoldError(err.message || 'Failed to update milestone status.');
    }
  };

  const openDeliverableModal = (task) => {
    setDeliverableTask(task);
    setDeliverableFile(null);
    setDeliverableNotes(task.submissionNotes || '');
    setDeliverableError('');
    setDeliverableModalOpen(true);
  };

  const submitDeliverable = async (e) => {
    e.preventDefault();
    if (!deliverableFile && !deliverableNotes.trim()) {
      setDeliverableError('Please attach a deliverable file or provide submission notes.');
      return;
    }

    try {
      setDeliverableSubmitting(true);
      const formData = new FormData();
      if (deliverableFile) {
        formData.append('deliverableFile', deliverableFile);
      }
      formData.append('submissionNotes', deliverableNotes.trim());

      await portalApi.submitStartupPortalTaskDeliverable(deliverableTask._id, formData);
      setDeliverableModalOpen(false);
      await loadRoadmap();
    } catch (err) {
      setDeliverableError(err.message || 'Failed to submit deliverable.');
    } finally {
      setDeliverableSubmitting(false);
    }
  };

  const COLUMNS = [
    { key: 'To Do', label: 'To Do', match: ['To Do', 'Pending'] },
    { key: 'In Progress', label: 'In Progress', match: ['In Progress'] },
    { key: 'On Hold', label: 'On Hold', match: ['On Hold'] },
    { key: 'In Review', label: 'In Review', match: ['In Review'] },
    { key: 'Done', label: 'Done', match: ['Done', 'Completed'] },
  ];

  const now = new Date();

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-white tracking-tight">Roadmap & Deliverables</h1>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#D42B92]/10 text-[#FF8EC5] border border-[#D42B92]/20">
              Cohort Acceleration
            </span>
          </div>
          <p className="text-sm text-white/60 mt-1">
            Program milestone tracker, deliverable submissions, and mentor advisory action items.
          </p>
        </div>

        {/* View Mode & Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadRoadmap()}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-white transition-colors"
            title="Refresh Roadmap"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Board / List switch */}
          <div className="flex items-center p-1 rounded-xl border border-[#2C2C34] bg-[#08080A]">
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === 'board' ? 'bg-[#321129] text-[#FF8EC5] border border-[#D42B92]/30 shadow-sm' : 'text-[#8E8E99] hover:text-[#F2F2F4]'
              }`}
            >
              <Kanban className="w-3.5 h-3.5" />
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === 'list' ? 'bg-[#321129] text-[#FF8EC5] border border-[#D42B92]/30 shadow-sm' : 'text-[#8E8E99] hover:text-[#F2F2F4]'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* ─── Roadmap Stage Stepper Strip matching mockup ─── */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6">
        <div className="text-sm sm:text-base font-bold text-[#F2F2F4] mb-6">
          Roadmap · Pilot stage · 60%
        </div>

        {/* Stepper with connecting lines */}
        <div className="relative flex items-center justify-between px-3 sm:px-8">
          {/* Background Gray Line (Full width) */}
          <div className="absolute left-6 right-6 top-2.5 h-0.5 bg-[#2C2C34] z-0" />

          {/* Active Pink Line (from Idea through MVP to Pilot = 50% width) */}
          <div className="absolute left-6 w-[50%] top-2.5 h-0.5 bg-[#D42B92] z-0" />

          {/* Step 1: Idea · Done */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-5 h-5 rounded-full bg-emerald-400 ring-2 ring-[#131318] shadow-sm shadow-emerald-400/30" />
            <span className="text-xs font-medium text-[#8E8E99] mt-2 whitespace-nowrap">
              Idea · Done
            </span>
          </div>

          {/* Step 2: MVP · Done */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-5 h-5 rounded-full bg-emerald-400 ring-2 ring-[#131318] shadow-sm shadow-emerald-400/30" />
            <span className="text-xs font-medium text-[#8E8E99] mt-2 whitespace-nowrap">
              MVP · Done
            </span>
          </div>

          {/* Step 3: Pilot · 60% (Active) */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-5 h-5 rounded-full bg-[#D42B92] ring-4 ring-[#D42B92]/30 shadow-md shadow-[#D42B92]/50" />
            <span className="text-xs font-bold text-white mt-2 whitespace-nowrap">
              Pilot · 60%
            </span>
          </div>

          {/* Step 4: GTM · Locked */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-5 h-5 rounded-full bg-[#2C2C34] ring-2 ring-[#131318]" />
            <span className="text-xs font-medium text-[#8E8E99] mt-2 whitespace-nowrap">
              GTM · Locked
            </span>
          </div>

          {/* Step 5: Scale · Locked */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-5 h-5 rounded-full bg-[#2C2C34] ring-2 ring-[#131318]" />
            <span className="text-xs font-medium text-[#8E8E99] mt-2 whitespace-nowrap">
              Scale · Locked
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-[#2C2C34]/60 pb-4">
        <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          <button
            onClick={() => handleFilterChange('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              activeFilter === 'all'
                ? 'bg-[#D42B92] text-white shadow-sm shadow-[#D42B92]/20'
                : 'bg-[#131318] border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4]'
            }`}
          >
            All Milestones ({metrics.total})
          </button>
          <button
            onClick={() => handleFilterChange('overdue')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              activeFilter === 'overdue'
                ? 'bg-rose-500 text-white shadow-sm shadow-rose-500/20'
                : 'bg-[#131318] border border-[#2C2C34] text-[#8E8E99] hover:text-white'
            }`}
          >
            Overdue ({metrics.overdue})
          </button>
          <button
            onClick={() => handleFilterChange('urgent')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              activeFilter === 'urgent'
                ? 'bg-amber-500 text-black font-bold shadow-sm shadow-amber-500/20'
                : 'bg-[#131318] border border-[#2C2C34] text-[#8E8E99] hover:text-white'
            }`}
          >
            Urgent / High Priority ({metrics.urgent})
          </button>
          <button
            onClick={() => handleFilterChange('in_review')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
              activeFilter === 'in_review'
                ? 'bg-sky-500 text-white shadow-sm shadow-sky-500/20'
                : 'bg-[#131318] border border-[#2C2C34] text-[#8E8E99] hover:text-white'
            }`}
          >
            Awaiting Review ({metrics.inReview})
          </button>
        </div>

        {/* Sort selector */}
        <div className="flex items-center gap-2 text-xs text-[#8E8E99]">
          <span>Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] text-xs focus:outline-none focus:border-[#D42B92]"
          >
            <option value="deadline">Due Date</option>
            <option value="priority">Priority</option>
          </select>
        </div>
      </div>

      {/* Main Roadmap Area: Kanban or List */}
      {viewMode === 'board' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 items-start">
          {COLUMNS.map((col) => {
            const colTasks = filteredTasks.filter((t) => {
              if (col.key === 'To Do') {
                return col.match.includes(t.status) || !t.status;
              }
              return col.match.includes(t.status);
            });

            return (
              <div
                key={col.key}
                className="rounded-2xl border border-[#2C2C34] bg-[#131318] p-3.5 flex flex-col min-h-[500px]"
              >
                {/* Column Header */}
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#2C2C34]/60">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        col.key === 'Done'
                          ? 'bg-emerald-400'
                          : col.key === 'In Review'
                          ? 'bg-amber-400'
                          : col.key === 'In Progress'
                          ? 'bg-sky-400'
                          : col.key === 'On Hold'
                          ? 'bg-rose-400'
                          : 'bg-[#8E8E99]'
                      }`}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#F2F2F4]">
                      {col.label}
                    </span>
                  </div>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#08080A] border border-[#2C2C34] text-[#8E8E99]">
                    {colTasks.length}
                  </span>
                </div>

                {/* Column Cards */}
                <div className="space-y-3 flex-1 overflow-y-auto">
                  {colTasks.length === 0 ? (
                    <div className="p-8 text-center text-[#8E8E99]/50 text-xs border border-dashed border-[#2C2C34] rounded-xl">
                      No items
                    </div>
                  ) : (
                    colTasks.map((task) => {
                      const isOverdue = task.deadline && new Date(task.deadline) < now && !['Done', 'Completed'].includes(task.status);

                      return (
                        <div
                          key={task._id}
                          className="p-3.5 rounded-xl border border-[#2C2C34] bg-[#08080A] hover:border-[#D42B92]/40 transition-all space-y-3 cursor-pointer group shadow-sm"
                          onClick={() => setSelectedTask(task)}
                        >
                          {/* Priority & Deliverable Tag */}
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                                task.priority === 'Urgent'
                                  ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                  : task.priority === 'High'
                                  ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                                  : task.priority === 'Low'
                                  ? 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                                  : 'bg-sky-500/10 text-sky-300 border border-sky-500/20'
                              }`}
                            >
                              {task.priority || 'Medium'}
                            </span>

                            {task.requiresDeliverable && (
                              <span
                                className="text-[10px] text-white/40 flex items-center gap-1"
                                title="Requires Deliverable Submission"
                              >
                                <Paperclip className="w-3 h-3 text-[#FF8EC5]" />
                                Deliverable
                              </span>
                            )}
                          </div>

                          {/* Title */}
                          <h4 className="text-xs font-semibold text-white group-hover:text-[#FF8EC5] transition-colors leading-snug line-clamp-2">
                            {task.title}
                          </h4>

                          {/* On Hold Reason Snippet */}
                          {task.status === 'On Hold' && task.statusReason && (
                            <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-[11px] text-rose-300 leading-tight">
                              <span className="font-semibold">Hold: </span>
                              {task.statusReason}
                            </div>
                          )}

                          {/* Reviewer Feedback Snippet */}
                          {task.reviewerFeedback && (
                            <div className="p-2 rounded-lg bg-[#08080A] border border-[#2C2C34] text-[11px] text-[#F2F2F4]/80 leading-tight">
                              <span className="font-semibold text-[#F2F2F4]">Reviewer: </span>
                              {task.reviewerFeedback}
                            </div>
                          )}

                          {/* Footer: Due date & stage */}
                          <div className="flex items-center justify-between text-[11px] text-[#8E8E99] pt-1.5 border-t border-[#2C2C34]/80">
                            <div
                              className={`flex items-center gap-1 ${
                                isOverdue ? 'text-rose-400 font-semibold' : ''
                              }`}
                            >
                              <Clock className="w-3 h-3" />
                              <span>
                                {task.deadline
                                  ? new Date(task.deadline).toLocaleDateString([], {
                                      month: 'short',
                                      day: 'numeric',
                                    })
                                  : 'No date'}
                              </span>
                            </div>

                            <span className="truncate max-w-[90px]">{task.stage || 'Milestone'}</span>
                          </div>

                          {/* Quick Action Buttons (Prevent card click propagation) */}
                          <div
                            className="pt-1 flex items-center justify-end gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(task.status === 'To Do' || !task.status || task.status === 'Pending') && (
                              <button
                                onClick={() => startTask(task)}
                                className="px-2 py-1 rounded bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 text-[10px] font-semibold flex items-center gap-1 transition-colors"
                              >
                                <Play className="w-2.5 h-2.5" />
                                Start
                              </button>
                            )}

                            {task.status === 'In Progress' && (
                              <>
                                <button
                                  onClick={() => openHoldModal(task)}
                                  className="px-2 py-1 rounded border border-[#2C2C34] hover:bg-[#181820] text-[#8E8E99] hover:text-[#F2F2F4] text-[10px] font-medium flex items-center gap-1 transition-colors"
                                  title="Place on hold"
                                >
                                  <PauseCircle className="w-2.5 h-2.5" />
                                  Hold
                                </button>
                                <button
                                  onClick={() => openDeliverableModal(task)}
                                  className="px-2.5 py-1 rounded bg-[#D42B92] hover:brightness-110 text-white text-[10px] font-semibold flex items-center gap-1 transition-all shadow-sm shadow-[#D42B92]/20"
                                >
                                  <Send className="w-2.5 h-2.5" />
                                  Submit
                                </button>
                              </>
                            )}

                            {task.status === 'On Hold' && (
                              <button
                                onClick={() => resumeTask(task)}
                                className="px-2.5 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-[10px] font-semibold flex items-center gap-1 transition-colors"
                              >
                                <Play className="w-2.5 h-2.5" />
                                Resume
                              </button>
                            )}

                            {task.status === 'In Review' && (
                              <span className="text-[10px] text-amber-400 font-medium flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                Under Review
                              </span>
                            )}

                            {['Done', 'Completed'].includes(task.status) && (
                              <span className="text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                                <CheckCircle className="w-2.5 h-2.5" />
                                Completed
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="rounded-2xl border border-[#2C2C34] bg-[#131318] overflow-hidden">
          <div className="p-3.5 border-b border-[#2C2C34] text-xs font-semibold text-[#8E8E99] uppercase tracking-wider grid grid-cols-12 gap-4">
            <div className="col-span-5">Milestone / Title</div>
            <div className="col-span-2">Priority / Stage</div>
            <div className="col-span-2">Deadline</div>
            <div className="col-span-3 text-right">Status & Action</div>
          </div>
          <div className="divide-y divide-white/5">
            {filteredTasks.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-sm">No tasks matching active criteria.</div>
            ) : (
              filteredTasks.map((task) => (
                <div
                  key={task._id}
                  onClick={() => setSelectedTask(task)}
                  className="p-4 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.015] cursor-pointer transition-colors"
                >
                  <div className="col-span-5">
                    <div className="text-sm font-semibold text-white">{task.title}</div>
                    <div className="text-xs text-white/50 line-clamp-1 mt-0.5">{task.description}</div>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                        task.priority === 'Urgent'
                          ? 'bg-red-500/10 text-red-400'
                          : task.priority === 'High'
                          ? 'bg-amber-500/10 text-amber-300'
                          : 'bg-white/5 text-white/50'
                      }`}
                    >
                      {task.priority || 'Medium'}
                    </span>
                    <div className="text-[11px] text-white/40">{task.stage}</div>
                  </div>
                  <div className="col-span-2 text-xs text-white/60">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'}
                  </div>
                  <div
                    className="col-span-3 flex items-center justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      className={`text-xs px-2.5 py-1 rounded-lg font-medium ${
                        ['Done', 'Completed'].includes(task.status)
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : task.status === 'In Review'
                          ? 'bg-amber-500/15 text-amber-300'
                          : task.status === 'In Progress'
                          ? 'bg-sky-500/15 text-sky-300'
                          : task.status === 'On Hold'
                          ? 'bg-rose-500/15 text-rose-300'
                          : 'bg-white/5 text-white/50'
                      }`}
                    >
                      {task.status || 'To Do'}
                    </span>

                    {task.status === 'In Progress' && (
                      <button
                        onClick={() => openDeliverableModal(task)}
                        className="px-2.5 py-1 rounded-lg bg-[#D42B92] text-white text-xs font-medium"
                      >
                        Submit
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-lg rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-5 text-[#F2F2F4]">
            <div className="flex items-start justify-between border-b border-[#2C2C34] pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                      selectedTask.priority === 'Urgent'
                        ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                        : selectedTask.priority === 'High'
                        ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                        : 'bg-white/5 text-white/50'
                    }`}
                  >
                    {selectedTask.priority || 'Medium'}
                  </span>
                  <span className="text-xs text-[#8E8E99]">• {selectedTask.stage}</span>
                </div>
                <h3 className="text-lg font-bold mt-1.5">{selectedTask.title}</h3>
              </div>
              <button
                onClick={() => setSelectedTask(null)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">Description</label>
                <p className="text-[#F2F2F4]/80 leading-relaxed bg-[#08080A] p-3 rounded-xl border border-[#2C2C34]">
                  {selectedTask.description || 'No detailed instructions provided.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34]">
                  <span className="text-[#8E8E99] block mb-1">Target Deadline</span>
                  <div className="font-semibold text-[#F2F2F4]">
                    {selectedTask.deadline ? new Date(selectedTask.deadline).toLocaleDateString() : 'None set'}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34]">
                  <span className="text-[#8E8E99] block mb-1">Current Status</span>
                  <div className="font-semibold text-[#D42B92]">{selectedTask.status || 'To Do'}</div>
                </div>
              </div>

              {/* Submitted Deliverable Info */}
              {selectedTask.deliverableUrl && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-1.5">
                  <div className="font-semibold text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" />
                    Submitted Deliverable
                  </div>
                  {selectedTask.submissionNotes && (
                    <p className="text-white/70 italic text-[11px]">"{selectedTask.submissionNotes}"</p>
                  )}
                  <a
                    href={resolveFileUrl(selectedTask.deliverableUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 hover:underline pt-1"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    View Attached Deliverable File
                  </a>
                </div>
              )}

              {/* Reviewer Feedback Callout */}
              {selectedTask.reviewerFeedback && (
                <div className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34] space-y-1">
                  <div className="font-semibold text-[#F2F2F4]">Reviewer Feedback:</div>
                  <p className="text-[#8E8E99] text-[11px]">{selectedTask.reviewerFeedback}</p>
                </div>
              )}

              {/* Status History Audit Trail */}
              {selectedTask.statusHistory && selectedTask.statusHistory.length > 0 && (
                <div>
                  <label className="text-[#8E8E99] font-medium block mb-1 flex items-center gap-1">
                    <History className="w-3.5 h-3.5 text-[#D42B92]" />
                    Status Transition History
                  </label>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {selectedTask.statusHistory.map((h, i) => (
                      <div
                        key={i}
                        className="p-2 rounded-lg bg-[#08080A] border border-[#2C2C34] flex items-center justify-between text-[11px]"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#F2F2F4]">{h.status}</span>
                          {h.reason && <span className="text-[#8E8E99] truncate max-w-[150px]">({h.reason})</span>}
                        </div>
                        <span className="text-[10px] text-[#8E8E99]">
                          {h.changedAt ? new Date(h.changedAt).toLocaleDateString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#2C2C34]">
              <button
                onClick={() => setSelectedTask(null)}
                className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Place On Hold Modal */}
      {holdModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <h3 className="text-base font-bold text-rose-300 flex items-center gap-2">
                <PauseCircle className="w-5 h-5 text-rose-400" />
                Place Milestone On Hold
              </h3>
              <button
                onClick={() => setHoldModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#8E8E99]">
              Please state why this milestone is paused (e.g. awaiting external vendor, attorney availability, or regulatory approval).
            </p>

            {holdError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
                {holdError}
              </div>
            )}

            <form onSubmit={submitHold} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#8E8E99] block mb-1">
                  Justification Reason *
                </label>
                <textarea
                  rows={3}
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  placeholder="e.g. Blocked on patent lawyer review until next Tuesday."
                  className="w-full px-3 py-2 rounded-xl border border-[#2C2C34] bg-[#08080A] text-xs text-[#F2F2F4] placeholder-[#8E8E99]/50 focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setHoldModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold"
                >
                  Confirm Hold
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Submit Deliverable Modal */}
      {deliverableModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-lg rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <div>
                <h3 className="text-base font-bold text-[#F2F2F4] flex items-center gap-2">
                  <Send className="w-4 h-4 text-[#D42B92]" />
                  Submit Deliverable for Review
                </h3>
                <p className="text-xs text-[#8E8E99] mt-0.5">{deliverableTask?.title}</p>
              </div>
              <button
                onClick={() => setDeliverableModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {deliverableError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
                {deliverableError}
              </div>
            )}

            <form onSubmit={submitDeliverable} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#8E8E99] block mb-1">
                  Submission Notes / Summary
                </label>
                <textarea
                  rows={3}
                  value={deliverableNotes}
                  onChange={(e) => setDeliverableNotes(e.target.value)}
                  placeholder="Outline what was accomplished, key conclusions, or links..."
                  className="w-full px-3 py-2 rounded-xl border border-[#2C2C34] bg-[#08080A] text-xs text-[#F2F2F4] placeholder-[#8E8E99]/50 focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-[#8E8E99] block mb-1">
                  Attach Deliverable File (PDF, DOC, PPT, ZIP, JPG)
                </label>
                <input
                  type="file"
                  onChange={(e) => setDeliverableFile(e.target.files[0] || null)}
                  className="w-full text-xs text-[#8E8E99] file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border file:border-[#2C2C34] file:text-xs file:font-semibold file:bg-[#08080A] file:text-[#F2F2F4] hover:file:border-[#D42B92] cursor-pointer"
                />
              </div>

              <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20 text-[11px] text-sky-200">
                Notice: Submitting moves this milestone into <span className="font-semibold">In Review</span>.
                Your assigned program director or mentor will inspect the deliverables and finalize completion.
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setDeliverableModalOpen(false)}
                  disabled={deliverableSubmitting}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deliverableSubmitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-semibold flex items-center gap-2 transition-all shadow-md shadow-[#D42B92]/20 disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>{deliverableSubmitting ? 'Uploading...' : 'Submit Deliverable'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
